import type { Name_lesson } from "../../types/course/detail"

export interface TreeNode {
	children: TreeNode[]
	currentLearn?: boolean
	duration: string | number
	icon: React.ReactNode
	id?: string
	is_preview?: boolean
	key: string
	name: string
	title: string
}

export class TreeDataConverter {
	static convertToSeconds(time: string): number {
		if (!time) return 0
		const [hours, minutes, seconds] = time.split(":").map(Number.parseFloat)
		return hours * 3600 + minutes * 60 + seconds
	}

	static convertToTimeFormat(seconds: number): string {
		const hours = Math.floor(seconds / 3600)
		const minutes = Math.floor((seconds % 3600) / 60)
		const secs = seconds % 60
		return [hours, minutes, secs]
			.map((val) => (val < 10 ? `0${val}` : val))
			.join(":")
	}

	static convertToTreeData(
		jsonData: Name_lesson[],
		lastLessonId: string,
	): TreeNode[] {
		const idMap: { [key: string]: Name_lesson & { children: Name_lesson[] } } =
			{}
		const parentMap: { [key: string]: TreeNode } = {}

		for (const item of jsonData) {
			idMap[item.id] = { ...item, children: [] }
		}

		for (const item of jsonData) {
			if (item.depend_id && idMap[item.depend_id]) {
				const parentId = item.depend_id
				const parentItem = idMap[parentId]

				if (!parentMap[parentId]) {
					parentMap[parentId] = {
						children: [],
						duration: 0,
						icon: "",
						id: parentId.toString(),
						key: `0-${parentId}`,
						name: parentItem.name,
						title: parentItem.name,
					}
				}

				const itemDurationSeconds = TreeDataConverter.convertToSeconds(
					item.duration,
				)
				parentMap[parentId].duration =
					(parentMap[parentId].duration as number) + itemDurationSeconds

				parentMap[parentId].children.push({
					children: [],
					currentLearn: item.id === lastLessonId,
					duration: item.duration,
					icon: "",
					id: item.id,
					is_preview: item.is_preview,
					key: `0-${parentId}-${item.id}`,
					name: parentItem.name,
					title: item.name,
				})
			} else if (!item.depend_id) {
				const itemDurationSeconds = TreeDataConverter.convertToSeconds(
					item.duration,
				)
				parentMap[item.id] = {
					children: [],
					currentLearn: item.id === lastLessonId,
					duration: itemDurationSeconds,
					id: `${item.id}`,
					icon: "",
					key: `0-${item.id}`,
					name: item.name,
					title: item.name,
				}
			}
		}

		for (const parent of Object.values(parentMap)) {
			parent.duration = TreeDataConverter.convertToTimeFormat(
				parent.duration as number,
			)

			parent.children.sort((a, b) => {
				return TreeDataConverter.naturalSort(a.title, b.title)
			})
		}

		const sortedParents = Object.values(parentMap).sort((a, b) => {
			return TreeDataConverter.naturalSort(a.title, b.title)
		})

		return sortedParents
	}
	static naturalSort(a: string, b: string): number {
		const regex = /(\d+)|(\D+)/g
		const aTokens = a.match(regex)
		const bTokens = b.match(regex)

		if (!aTokens || !bTokens) return a.localeCompare(b)

		for (let i = 0; i < Math.min(aTokens.length, bTokens.length); i++) {
			const aToken = aTokens[i]
			const bToken = bTokens[i]

			const aNum = Number.parseInt(aToken)
			const bNum = Number.parseInt(bToken)

			if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
				if (aNum !== bNum) return aNum - bNum
			} else {
				const compared = aToken.localeCompare(bToken)
				if (compared !== 0) return compared
			}
		}

		return aTokens.length - bTokens.length
	}

	static getTotalDuration(lessons: Name_lesson[]): number {
		return lessons.reduce((total, lesson) => {
			return total + TreeDataConverter.convertToSeconds(lesson.duration)
		}, 0)
	}
	static convertSecondsToTimeFormat(seconds: number): string {
		const hours = Math.floor(seconds / 3600)
		const minutes = Math.floor((seconds % 3600) / 60)
		const secs = seconds % 60
		return [hours, minutes, secs]
			.map((val) => (val < 10 ? `0${val}` : val))
			.join(":")
	}
	static convertSecondsToHours(seconds: number): string {
		const hours = Math.floor(seconds / 3600)
		return `${hours} giờ`
	}
	static getLastLesson(lessons: Name_lesson[]): string {
		const data = lessons.find((lesson) => !lesson.learned)
		if (!data) return "1"
		return data.id
	}
	static getKeyFromId(lessons: Name_lesson[], id: string): string {
		const lesson = lessons.find((l) => l.id === id)
		if (!lesson) return ""

		if (lesson.depend_id) {
			return `0-${lesson.depend_id}-${id}`
		}
		return `0-${id}`
	}
}
